// src/pages/MaritimeSentry.jsx
// Maritime Sentry - AI-Powered Vessel Operations
// V3 Skin: Warm white · Gold rule · Georgia serif · Category color rules · Status pills

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// V3 DESIGN TOKENS
// ════════════════════════════════════════════════════════════════════════════
const T = {
  bg:            '#F5F3EF',
  bgCard:        '#FFFFFF',
  bgElevated:    '#FAFAF8',
  bgCanvas:      '#EDE9E2',
  border:        '#E2DDD6',
  borderStrong:  '#C8C2B8',
  textPrimary:   '#1A1814',
  textSecondary: '#5C5850',
  textMuted:     '#9C9890',
  gold:          '#9A7B2E',
  goldLight:     '#C4992A',
  goldBg:        'rgba(154,123,46,0.06)',
  goldBorder:    'rgba(154,123,46,0.18)',
  red:           '#8B1A1A',
  redLight:      '#B32020',
  redBg:         'rgba(139,26,26,0.06)',
  redBorder:     'rgba(139,26,26,0.18)',
  green:         '#1A4D2E',
  greenLight:    '#1E6B3A',
  greenBg:       'rgba(26,77,46,0.06)',
  greenBorder:   'rgba(26,77,46,0.18)',
  amber:         '#7A5C0A',
  amberLight:    '#9A7210',
  amberBg:       'rgba(122,92,10,0.06)',
  amberBorder:   'rgba(122,92,10,0.18)',
  navy:          '#1A2D5A',
  navyBg:        'rgba(26,45,90,0.06)',
  navyBorder:    'rgba(26,45,90,0.18)',
};

// ════════════════════════════════════════════════════════════════════════════
// CATEGORY STYLES — institutional palette
// ════════════════════════════════════════════════════════════════════════════
const CATEGORY_STYLES = {
  propulsion:     { icon: '🚢', color: '#2E6EA6', label: 'Propulsion'    },
  navigation:     { icon: '⚓', color: '#1A2D5A', label: 'Navigation'    },
  power:          { icon: '🔌', color: '#7A5C0A', label: 'Power'         },
  cargo:          { icon: '📦', color: '#1A4D2E', label: 'Cargo'         },
  stability:      { icon: '🌊', color: '#5B4E8A', label: 'Stability'     },
  bunkers:        { icon: '⛽', color: '#9A7B2E', label: 'Bunkers'       },
  hull:           { icon: '🚪', color: '#4A3C38', label: 'Hull'          },
  safety:         { icon: '🔥', color: '#8B1A1A', label: 'Safety'        },
  communications: { icon: '📡', color: '#2E4A7A', label: 'Comms'         },
};

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS
// ════════════════════════════════════════════════════════════════════════════
const AI_STATUS = {
  CRITICAL:   { text: 'CRITICAL',   bg: T.redBg,   border: T.redBorder,   color: T.redLight,   dot: T.redLight,   pulse: true  },
  WARNING:    { text: 'WARNING',    bg: T.amberBg, border: T.amberBorder, color: T.amberLight, dot: T.amberLight, pulse: false },
  MONITORING: { text: 'MONITORING', bg: T.navyBg,  border: T.navyBorder,  color: T.navy,       dot: T.navy,       pulse: false },
  NORMAL:     { text: 'NORMAL',     bg: T.greenBg, border: T.greenBorder, color: T.greenLight, dot: T.greenLight, pulse: false },
  STANDBY:    { text: 'STANDBY',    bg: 'rgba(156,152,144,0.08)', border: 'rgba(156,152,144,0.2)', color: T.textMuted, dot: T.textMuted, pulse: false },
};

// ════════════════════════════════════════════════════════════════════════════
// MOCK DATA (unchanged from original)
// ════════════════════════════════════════════════════════════════════════════
const MOCK_VESSEL_GATEWAY = {
  id: 'gateway-pacific-voyager',
  name: 'MV Pacific Voyager',
  imo: 'IMO 9876543',
  mmsi: '538006789',
  callSign: 'V7AB9',
  flag: '🇱🇷 Liberia',
  vesselType: 'Container Ship',
  class: 'Post-Panamax',
  status: 'At Sea',
  location: '32°45\'N 142°30\'W',
  heading: 285,
  speed: 18.4,
  destination: 'Los Angeles',
  eta: '2025-02-02T14:00:00Z',
  voyage: 'V-2025-0042',
  teu: 8500,
  containersLoaded: 6842,
  reeferCount: 234,
  draft: { forward: 12.4, aft: 13.1, midship: 12.8 },
  stratusModel: 'Stratus 5G Maritime',
  connectivity: 'VSAT + LTE Maritime',
  lastSync: new Date().toISOString(),
  crewOnboard: 24,
  nextPort: 'Los Angeles, CA',
  distanceToGo: 847,
};

const MOCK_SYSTEMS = [
  { id: 'ME-01', type: 'Main Engine', category: 'propulsion', model: 'MAN B&W 8G95ME-C10.5', location: 'Engine Room - Port', status: 'running',
    metrics: { rpm: 72, power: 42500, maxPower: 48000, load: 88.5, fuelRate: 142.5, cylExhaustTemps: [385, 392, 388, 456, 390, 387, 391, 389], avgExhaustTemp: 397, scavAirPressure: 3.2, turboSpeed: 9450, lubOilPressure: 4.8, coolantTemp: 78, runHours: 48250 } },
  { id: 'ME-02', type: 'Main Engine', category: 'propulsion', model: 'MAN B&W 8G95ME-C10.5', location: 'Engine Room - Starboard', status: 'running',
    metrics: { rpm: 72, power: 42200, maxPower: 48000, load: 87.9, fuelRate: 141.8, cylExhaustTemps: [382, 385, 380, 384, 388, 383, 386, 381], avgExhaustTemp: 384, scavAirPressure: 3.1, turboSpeed: 9380, lubOilPressure: 4.9, coolantTemp: 76, runHours: 47890 } },
  { id: 'BOW-THRUSTER', type: 'Bow Thruster', category: 'propulsion', model: 'Rolls-Royce TT2400', location: 'Bow - Below Waterline', status: 'standby',
    metrics: { power: 0, maxPower: 2400, hydraulicPressure: 210, oilTemp: 42, lastUsed: '2025-01-28T06:45:00Z', runHours: 1245 } },
  { id: 'NAV-GPS', type: 'GPS/DGPS', category: 'navigation', model: 'Furuno GP-170', location: 'Bridge - Overhead', status: 'operational',
    metrics: { satellites: 12, hdop: 0.8, position: { lat: 32.75, lon: -142.50 }, sog: 18.4, cog: 285, accuracy: 2.1 } },
  { id: 'NAV-AIS', type: 'AIS Transponder', category: 'navigation', model: 'Kongsberg AI50', location: 'Bridge', status: 'warning',
    metrics: { class: 'A', txPower: 12.5, position: { lat: 32.7503, lon: -142.4997 }, positionDelta: 0.31, lastTx: '2025-01-31T15:00:00Z', messagesRx: 4521, nearbyVessels: 8 } },
  { id: 'NAV-RADAR', type: 'X-Band Radar', category: 'navigation', model: 'Furuno FAR-2228', location: 'Mast - Upper', status: 'operational',
    metrics: { range: 24, targets: 14, seaClutter: 'Auto', rainClutter: 15, heading: 285, rotation: 24 } },
  { id: 'NAV-ECDIS', type: 'ECDIS', category: 'navigation', model: 'Transas Navi-Sailor 4000', location: 'Bridge - Main Console', status: 'operational',
    metrics: { chartStatus: 'Updated', routeActive: 'LA-APPROACH-V42', waypointsRemaining: 3, xtd: 0.12, dtw: 847, eta: '2025-02-02T14:00:00Z' } },
  { id: 'GEN-01', type: 'Diesel Generator', category: 'power', model: 'Caterpillar 3516C', location: 'Engine Room - Generator Flat', status: 'running',
    metrics: { power: 1850, maxPower: 2500, load: 74, voltage: 6600, frequency: 60, fuelRate: 385, coolantTemp: 82, runHours: 18450 } },
  { id: 'GEN-02', type: 'Diesel Generator', category: 'power', model: 'Caterpillar 3516C', location: 'Engine Room - Generator Flat', status: 'running',
    metrics: { power: 1720, maxPower: 2500, load: 68.8, voltage: 6600, frequency: 60, fuelRate: 358, coolantTemp: 79, runHours: 16890 } },
  { id: 'GEN-EMCY', type: 'Emergency Generator', category: 'power', model: 'Volvo Penta D16', location: 'Emergency Gen Room - Deck 4', status: 'standby',
    metrics: { power: 0, maxPower: 800, batteryStatus: 98, lastTest: '2025-01-28T10:00:00Z', testResult: 'PASS', startTime: 8.2 } },
  { id: 'REEFER-BAY42', type: 'Reefer Monitoring', category: 'cargo', model: 'Carrier PrimeLINE', location: 'Bay 42 - On Deck', status: 'alarm',
    metrics: { containerId: 'MSCU7234521', setpoint: -25, actualTemp: -12, tempDeviation: 13, humidity: 45, supplyAir: -28, returnAir: -10, defrostStatus: 'Off', powerStatus: 'Connected', alarmTime: '2025-01-31T13:45:00Z', cargoType: 'Frozen Seafood', cargoValue: 285000 } },
  { id: 'REEFER-BAY26', type: 'Reefer Monitoring', category: 'cargo', model: 'Carrier PrimeLINE', location: 'Bay 26 - Hold', status: 'operational',
    metrics: { containerId: 'TCLU4892156', setpoint: 2, actualTemp: 2.3, tempDeviation: 0.3, humidity: 85, supplyAir: 0, returnAir: 3, defrostStatus: 'Off', powerStatus: 'Connected', cargoType: 'Fresh Produce', cargoValue: 125000 } },
  { id: 'CARGO-LASHING', type: 'Lashing Monitor', category: 'cargo', model: 'MacGregor AutoLash', location: 'Deck - All Bays', status: 'operational',
    metrics: { sensorsOnline: 847, sensorsTotal: 850, tensionAlerts: 2, maxTension: 78, avgTension: 52, lastCheck: '2025-01-31T08:00:00Z' } },
  { id: 'BALLAST-SYS', type: 'Ballast System', category: 'stability', model: 'Alfa Laval PureBallast', location: 'Engine Room - Tank Top', status: 'warning',
    metrics: { tanks: { '1P': { capacity: 1850, level: 45, status: 'OK' }, '1S': { capacity: 1850, level: 48, status: 'OK' }, '2P': { capacity: 2100, level: 62, status: 'OK' }, '2S': { capacity: 2100, level: 58, status: 'OK' }, '3P': { capacity: 2400, level: 94, status: 'ANOMALY' }, '3S': { capacity: 2400, level: 12, status: 'OK' }, '4P': { capacity: 1950, level: 0, status: 'OK' }, '4S': { capacity: 1950, level: 0, status: 'OK' }, 'APT': { capacity: 850, level: 78, status: 'OK' }, 'FPT': { capacity: 620, level: 65, status: 'OK' } }, totalBallast: 8450, treatmentStatus: 'Active', pumpStatus: 'Idle', lastTransfer: '2025-01-30T22:00:00Z' } },
  { id: 'LOADICATOR', type: 'Loading Computer', category: 'stability', model: 'NAPA Loading Computer', location: 'Bridge / Cargo Office', status: 'operational',
    metrics: { displacement: 98450, gm: 2.45, trim: 0.7, heel: 0.2, shearForce: 68, bendingMoment: 72, stability: 'ADEQUATE', draftFwd: 12.4, draftAft: 13.1 } },
  { id: 'BUNKER-HFO', type: 'Heavy Fuel Oil', category: 'bunkers', model: 'Tank Monitoring System', location: 'Double Bottom Tanks', status: 'operational',
    metrics: { totalCapacity: 4200, currentLevel: 2845, consumption24h: 285, voyagePlan: 241, consumptionVariance: 18.3, daysRemaining: 9.8, lastBunkering: '2025-01-25T08:00:00Z', sulfurContent: 0.48, viscosity: 42 } },
  { id: 'BUNKER-MDO', type: 'Marine Diesel Oil', category: 'bunkers', model: 'Tank Monitoring System', location: 'Settling/Service Tanks', status: 'operational',
    metrics: { totalCapacity: 450, currentLevel: 312, consumption24h: 18, daysRemaining: 17.3 } },
  { id: 'WTD-SYSTEM', type: 'Watertight Doors', category: 'hull', model: 'TTS Marine WTD', location: 'All Decks', status: 'warning',
    metrics: { totalDoors: 18, doorsSecured: 17, doorsFault: 1, faultDetails: { door: 'WTD-14', location: 'Engine Room - Fwd Bulkhead', status: 'Failed closure test', lastTest: '2025-01-31T12:00:00Z', hydraulicPressure: 145 }, autoCloseEnabled: true, lastDrill: '2025-01-28T14:00:00Z' } },
  { id: 'HULL-STRESS', type: 'Hull Stress Monitor', category: 'hull', model: 'Strainstall Hull Monitor', location: 'Key Structural Points', status: 'operational',
    metrics: { sensorsOnline: 24, maxStress: 65, midshipStress: 58, bowStress: 62, sternStress: 48, fatigueDamage: 12.4, waveHeight: 2.8 } },
  { id: 'FIRE-DETECTION', type: 'Fire Detection System', category: 'safety', model: 'Consilium Salwico', location: 'All Spaces', status: 'alarm',
    metrics: { zones: 48, detectorsOnline: 245, detectorsTotal: 248, activeAlarms: 1, alarmDetails: { zone: 'Engine Room Zone 4', type: 'Smoke', detector: 'SD-ER-047', time: '2025-01-31T14:58:00Z', status: 'INVESTIGATING' }, co2Status: 'Armed', sprinklerStatus: 'Ready' } },
  { id: 'LIFESAVING', type: 'Life-Saving Appliances', category: 'safety', model: 'Various / SOLAS', location: 'All Stations', status: 'operational',
    metrics: { lifeboats: { total: 2, ready: 2, capacity: 60 }, rescueBoat: { total: 1, ready: 1 }, liferafts: { total: 8, ready: 8, capacity: 200 }, epirb: { status: 'Armed', battery: 98, lastTest: '2025-01-15' }, sart: { status: 'Ready', battery: 95 }, immersionSuits: { total: 30, serviceable: 30 }, lastDrill: '2025-01-25T10:00:00Z', nextDrill: '2025-02-01T10:00:00Z' } },
  { id: 'COMM-VSAT', type: 'VSAT Terminal', category: 'communications', model: 'Intellian v100', location: 'Mast - Platform', status: 'warning',
    metrics: { signalStrength: -8.2, normalSignal: -4.5, throughputUp: 512, throughputDown: 2048, latency: 650, packetLoss: 4.2, antennaAzimuth: 185, antennaElevation: 42, satelliteId: 'INTELSAT-29e', dataUsed: 145 } },
  { id: 'COMM-GMDSS', type: 'GMDSS Station', category: 'communications', model: 'Sailor 6000 Series', location: 'Bridge - Radio Station', status: 'operational',
    metrics: { inmarsatC: 'Online', vhfDsc: 'Online', mfHfDsc: 'Online', navtex: 'Receiving', distressAlerts: 0, lastDscTest: '2025-01-30T08:00:00Z', batteryBackup: 96 } },
];

// ════════════════════════════════════════════════════════════════════════════
// ANOMALY DETECTION (unchanged logic)
// ════════════════════════════════════════════════════════════════════════════
const detectMaritimeAnomalies = (systems, vessel) => {
  const anomalies = [];
  systems.forEach(system => {
    const m = system.metrics;
    if (system.type === 'Main Engine' && m.cylExhaustTemps) {
      const maxTemp = Math.max(...m.cylExhaustTemps);
      const deviation = maxTemp - m.avgExhaustTemp;
      if (deviation > 50) {
        const hotCyl = m.cylExhaustTemps.indexOf(maxTemp) + 1;
        anomalies.push({ id: `me-cyl-${system.id}`, type: 'CYLINDER_OVERTEMP', systemId: system.id, severity: deviation > 70 ? 'critical' : 'warning', metric: `Cyl ${hotCyl}: ${maxTemp}°C (+${deviation}°C)`, description: `Cylinder ${hotCyl} exhaust temperature ${deviation}°C above average. Possible fuel injector failure or timing issue.`, recommendation: 'Reduce engine load. Inspect fuel injector and timing for cylinder ' + hotCyl + '. Schedule maintenance at next port.', confidence: 92, action: { type: 'REDUCE_LOAD', description: 'Reduce ME load 15%' } });
      }
    }
    if (system.type === 'AIS Transponder' && m.positionDelta > 0.2) {
      anomalies.push({ id: `nav-pos-${system.id}`, type: 'POSITION_MISMATCH', systemId: system.id, severity: m.positionDelta > 0.5 ? 'critical' : 'warning', metric: `${m.positionDelta.toFixed(2)} nm delta`, description: `GPS/AIS position discrepancy of ${m.positionDelta.toFixed(2)} nm detected. Possible GPS spoofing, AIS fault, or antenna issue.`, recommendation: 'Cross-check with radar fixes and visual bearings. Verify GPS antenna connections. Consider AIS inspection.', confidence: 88, action: { type: 'VERIFY_POSITION', description: 'Request bridge verification' } });
    }
    if (system.type === 'Reefer Monitoring' && m.tempDeviation > 3) {
      anomalies.push({ id: `reefer-${system.id}`, type: 'REEFER_TEMP_ALARM', systemId: system.id, severity: m.tempDeviation > 10 ? 'critical' : 'warning', metric: `${m.actualTemp}°C (SP: ${m.setpoint}°C)`, description: `Reefer ${m.containerId} temperature ${m.tempDeviation}°C above setpoint. ${m.cargoType} at risk. Value: $${(m.cargoValue || 0).toLocaleString()}.`, recommendation: m.tempDeviation > 10 ? 'URGENT: Dispatch crew immediately. Check power connection, compressor, and refrigerant levels.' : 'Monitor closely. Check airflow and defrost cycle.', confidence: 97, action: { type: 'DISPATCH_CREW', description: 'Send reefer technician' } });
    }
    if (system.type === 'Ballast System' && m.tanks) {
      Object.entries(m.tanks).forEach(([tankId, tank]) => {
        if (tank.status === 'ANOMALY') {
          anomalies.push({ id: `ballast-${system.id}-${tankId}`, type: 'BALLAST_ANOMALY', systemId: system.id, severity: 'warning', metric: `Tank ${tankId}: ${tank.level}%`, description: `Ballast tank ${tankId} showing ${tank.level}% but operations log indicates it should be empty.`, recommendation: 'Manually sound tank. Check valve positions. Review ballast log for unrecorded operations.', confidence: 85, action: { type: 'SOUND_TANK', description: 'Request manual verification' } });
        }
      });
    }
    if (system.type === 'Heavy Fuel Oil' && m.consumptionVariance > 15) {
      anomalies.push({ id: `fuel-${system.id}`, type: 'FUEL_CONSUMPTION_HIGH', systemId: system.id, severity: m.consumptionVariance > 25 ? 'critical' : 'warning', metric: `+${m.consumptionVariance.toFixed(1)}% over plan`, description: `Fuel consumption ${m.consumptionVariance.toFixed(1)}% above voyage plan. May indicate hull fouling, suboptimal trim, or engine inefficiency.`, recommendation: 'Review trim optimization. Check hull performance. Consider weather routing alternatives.', confidence: 89, action: { type: 'OPTIMIZE_VOYAGE', description: 'Run voyage optimization' } });
    }
    if (system.type === 'Watertight Doors' && m.doorsFault > 0) {
      anomalies.push({ id: `wtd-${system.id}`, type: 'WTD_FAULT', systemId: system.id, severity: 'critical', metric: `${m.doorsFault} door(s) fault`, description: `Watertight door ${m.faultDetails.door} at ${m.faultDetails.location} failed closure test. Hydraulic pressure: ${m.faultDetails.hydraulicPressure} bar. SOLAS compliance issue.`, recommendation: 'IMMEDIATE: Repair required before arrival. Check hydraulic system pressure. Inspect door seals and mechanism.', confidence: 98, action: { type: 'EMERGENCY_REPAIR', description: 'Priority repair order' } });
    }
    if (system.type === 'Fire Detection System' && m.activeAlarms > 0) {
      anomalies.push({ id: `fire-${system.id}`, type: 'FIRE_ALARM', systemId: system.id, severity: 'critical', metric: m.alarmDetails.zone, description: `${m.alarmDetails.type} alarm in ${m.alarmDetails.zone}. Detector: ${m.alarmDetails.detector}. Status: ${m.alarmDetails.status}.`, recommendation: 'IMMEDIATE INVESTIGATION REQUIRED. Dispatch fire team. Stand by fire suppression. Notify Master.', confidence: 99, action: { type: 'FIRE_RESPONSE', description: 'Initiate fire protocol' } });
    }
    if (system.type === 'VSAT Terminal' && (m.signalStrength < m.normalSignal - 2 || m.packetLoss > 3)) {
      anomalies.push({ id: `vsat-${system.id}`, type: 'COMM_DEGRADED', systemId: system.id, severity: 'warning', metric: `${m.signalStrength} dBm / ${m.packetLoss}% loss`, description: `VSAT link degraded. Signal: ${m.signalStrength} dBm (normal: ${m.normalSignal}). Packet loss: ${m.packetLoss}%.`, recommendation: 'Check antenna tracking. Verify no obstructions. Prepare Inmarsat backup.', confidence: 82 });
    }
  });
  return anomalies.sort((a, b) => ({ critical: 0, warning: 1, info: 2 }[a.severity] - { critical: 0, warning: 1, info: 2 }[b.severity]));
};

const getSystemAIStatus = (system, anomalies) => {
  const sysAnomalies = anomalies.filter(a => a.systemId === system.id);
  if (system.status === 'standby') return AI_STATUS.STANDBY;
  if (sysAnomalies.some(a => a.severity === 'critical') || system.status === 'alarm') return AI_STATUS.CRITICAL;
  if (sysAnomalies.some(a => a.severity === 'warning') || system.status === 'warning') return AI_STATUS.WARNING;
  return AI_STATUS.NORMAL;
};

// ════════════════════════════════════════════════════════════════════════════
// STATUS PILL — V3
// ════════════════════════════════════════════════════════════════════════════
const StatusPill = ({ status }) => {
  const map = {
    running:     { label: 'Running',     bg: T.greenBg,  border: T.greenBorder,  color: T.greenLight,  pulse: false },
    operational: { label: 'Operational', bg: T.greenBg,  border: T.greenBorder,  color: T.greenLight,  pulse: false },
    warning:     { label: 'Warning',     bg: T.amberBg,  border: T.amberBorder,  color: T.amberLight,  pulse: false },
    alarm:       { label: 'Alarm',       bg: T.redBg,    border: T.redBorder,    color: T.redLight,    pulse: true  },
    standby:     { label: 'Standby',     bg: 'rgba(156,152,144,0.08)', border: 'rgba(156,152,144,0.2)', color: T.textMuted, pulse: false },
  };
  const st = map[status] || map.standby;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 7px', borderRadius: 10,
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
      background: st.bg, border: `1px solid ${st.border}`, color: st.color,
    }}>
      <span style={{ width: 4, height: 4, borderRadius: '50%', background: st.color, display: 'inline-block', animation: st.pulse ? 'pulse 1.5s infinite' : 'none' }} />
      {st.label}
    </span>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// FLEET OPS NODE — V3
// ════════════════════════════════════════════════════════════════════════════
const FleetOpsNode = ({ data }) => (
  <div style={{ position: 'relative' }}>
    <Handle type="source" position={Position.Bottom} style={{ width: 10, height: 10, background: T.navy, border: `2px solid ${T.bgCard}` }} />
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`,
      borderRadius: 10, padding: '16px 20px', minWidth: 260,
      boxShadow: '0 4px 20px rgba(26,24,20,0.10)',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Gold top rule */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: T.goldLight, borderRadius: '10px 10px 0 0' }} />
      {/* Live dot */}
      <div style={{ position: 'absolute', top: 10, right: 10, width: 8, height: 8, borderRadius: '50%', background: T.greenLight, animation: 'pulse 1.5s infinite' }} />
      <div style={{ textAlign: 'center', marginBottom: 12, marginTop: 4 }}>
        <div style={{ fontSize: 28, marginBottom: 4 }}>🌐</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{data.label}</div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{data.sublabel}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
        {[
          { label: 'Vessels', value: data.vessels || 0, color: T.navy },
          { label: 'At Sea',  value: data.atSea || 0,   color: T.greenLight },
          { label: 'Alerts',  value: data.alerts || 0,  color: data.alerts > 0 ? T.redLight : T.textMuted },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.color, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 8, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginTop: 10 }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.gold, animation: 'pulse 2s infinite' }} />
        <span style={{ fontSize: 9, color: T.gold, fontWeight: 600 }}>Fleet Tracking Active</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// VESSEL GATEWAY NODE — V3
// ════════════════════════════════════════════════════════════════════════════
const VesselGatewayNode = ({ data }) => (
  <div style={{ position: 'relative' }}>
    <Handle type="target" position={Position.Top} style={{ width: 10, height: 10, background: T.greenLight, border: `2px solid ${T.bgCard}` }} />
    <Handle type="source" position={Position.Bottom} style={{ width: 10, height: 10, background: T.navy, border: `2px solid ${T.bgCard}` }} />
    <div style={{
      background: T.bgCard, border: `1px solid ${T.border}`,
      borderRadius: 10, padding: '14px 16px', minWidth: 320,
      boxShadow: '0 4px 20px rgba(26,24,20,0.10)', position: 'relative', overflow: 'hidden',
    }}>
      {/* Navy top rule */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: T.navy, borderRadius: '10px 10px 0 0' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, marginBottom: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: 8, background: T.navyBg, border: `1px solid ${T.navyBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🚢</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>{data.name}</div>
          <div style={{ fontSize: 10, color: T.textMuted }}>{data.imo} · {data.flag}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.navy, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{data.speed} kn</div>
          <div style={{ fontSize: 9, color: T.textMuted }}>HDG {data.heading}°</div>
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginBottom: 4 }}>
          <span style={{ color: T.textMuted }}>→ {data.destination}</span>
          <span style={{ color: T.navy, fontWeight: 700, fontFamily: 'Georgia, serif' }}>{data.distanceToGo} nm</span>
        </div>
        <div style={{ height: 4, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: T.navy, borderRadius: 2, width: '65%' }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>
        {[
          { label: 'TEU', value: data.containersLoaded?.toLocaleString(), color: T.gold },
          { label: 'Reefers', value: data.reeferCount, color: T.navy },
          { label: 'Draft', value: `${data.draft?.aft}m`, color: T.textSecondary },
          { label: 'Crew', value: data.crewOnboard, color: T.textSecondary },
        ].map(s => (
          <div key={s.label}>
            <div style={{ fontSize: 8, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: s.color, fontFamily: 'Georgia, serif' }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 9, color: T.textMuted, fontFamily: 'monospace' }}>{data.stratusModel} · {data.connectivity}</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// SHIP SYSTEM NODE — V3 with category color rule + status pill
// ════════════════════════════════════════════════════════════════════════════
const ShipSystemNode = ({ data }) => {
  const cat = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.propulsion;
  const aiStatus = data.aiStatus || AI_STATUS.NORMAL;
  const m = data.metrics || {};

  const renderMetrics = () => {
    const metricCell = (value, label, warn = false) => (
      <div style={{ textAlign: 'center', background: warn ? T.redBg : T.bgElevated, border: `1px solid ${warn ? T.redBorder : T.border}`, borderRadius: 4, padding: '3px 4px' }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: warn ? T.redLight : T.textPrimary, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 7, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 1 }}>{label}</div>
      </div>
    );
    switch (data.category) {
      case 'propulsion':
        if (data.type === 'Main Engine') {
          const maxCylTemp = m.cylExhaustTemps ? Math.max(...m.cylExhaustTemps) : 0;
          const tempAlert = maxCylTemp > m.avgExhaustTemp + 50;
          return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>{metricCell(`${m.load}%`, 'Load')}{metricCell(`${maxCylTemp}°C`, 'Exh Max', tempAlert)}{metricCell(m.rpm, 'RPM')}</div>;
        }
        return <div style={{ textAlign: 'center', fontSize: 10, color: T.textMuted, padding: '4px 0' }}>STANDBY</div>;
      case 'navigation':
        if (data.type === 'GPS/DGPS') return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>{metricCell(m.satellites, 'Sats')}{metricCell(m.sog, 'SOG')}{metricCell(`${m.cog}°`, 'COG')}</div>;
        if (data.type === 'AIS Transponder') { const posAlert = m.positionDelta > 0.2; return metricCell(`${m.positionDelta?.toFixed(2)}nm`, 'GPS Δ', posAlert); }
        return null;
      case 'cargo':
        if (data.type === 'Reefer Monitoring') { const tempAlert = m.tempDeviation > 3; return <div>{metricCell(`${m.actualTemp}°C`, 'Actual', tempAlert)}<div style={{ fontSize: 8, color: T.textMuted, textAlign: 'center', marginTop: 2, fontFamily: 'monospace' }}>{m.containerId}</div></div>; }
        return null;
      case 'stability':
        if (data.type === 'Loading Computer') return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>{metricCell(`${m.gm}m`, 'GM')}{metricCell(`${m.trim}m`, 'Trim')}{metricCell('OK', 'Stab')}</div>;
        return null;
      case 'bunkers':
        const fuelAlert = m.consumptionVariance > 15;
        return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>{metricCell(`${m.currentLevel}`, 'm³')}{metricCell(fuelAlert ? `+${m.consumptionVariance?.toFixed(0)}%` : 'OK', 'Cons', fuelAlert)}</div>;
      case 'hull':
        if (data.type === 'Watertight Doors') { const wtdAlert = m.doorsFault > 0; return metricCell(wtdAlert ? `${m.faultDetails?.door} FAULT` : `${m.doorsSecured}/${m.totalDoors}`, 'Doors', wtdAlert); }
        return null;
      case 'safety':
        if (data.type === 'Fire Detection System') { const fireAlert = m.activeAlarms > 0; return metricCell(fireAlert ? m.alarmDetails?.zone : 'All Clear', 'Status', fireAlert); }
        return null;
      case 'communications':
        if (data.type === 'VSAT Terminal') { const commAlert = m.packetLoss > 3; return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>{metricCell(`${m.signalStrength}`, 'dBm', commAlert)}{metricCell(`${m.packetLoss}%`, 'Loss', commAlert)}</div>; }
        return null;
      case 'power':
        if (data.type === 'Diesel Generator' && data.status === 'running') return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>{metricCell(`${m.power}`, 'kW')}{metricCell(`${m.load}%`, 'Load')}{metricCell(`${m.frequency}`, 'Hz')}</div>;
        return <div style={{ textAlign: 'center', fontSize: 10, color: T.textMuted, padding: '4px 0' }}>STANDBY</div>;
      default: return null;
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Handle type="target" position={Position.Top} style={{ width: 8, height: 8, background: cat.color, border: `2px solid ${T.bgCard}` }} />
      <div style={{
        background: T.bgCard, border: `1px solid ${T.border}`,
        borderRadius: 8, padding: '10px 10px 8px', minWidth: 148,
        boxShadow: '0 2px 8px rgba(26,24,20,0.07)', position: 'relative', overflow: 'hidden',
        transition: 'box-shadow 0.2s',
      }}>
        {/* Category color rule */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: cat.color, borderRadius: '8px 8px 0 0' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6, marginTop: 4, marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 14 }}>{cat.icon}</span>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.textPrimary, lineHeight: 1 }}>{data.id}</div>
              <div style={{ fontSize: 8, color: cat.color, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{cat.label}</div>
            </div>
          </div>
          {/* AI Status pill */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 3,
            padding: '2px 5px', borderRadius: 8, flexShrink: 0,
            background: aiStatus.bg, border: `1px solid ${aiStatus.border}`,
          }}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', background: aiStatus.dot, animation: aiStatus.pulse ? 'pulse 1.5s infinite' : 'none' }} />
            <span style={{ fontSize: 8, fontWeight: 700, color: aiStatus.color, letterSpacing: '0.04em' }}>{aiStatus.text}</span>
          </div>
        </div>

        {/* Type */}
        <div style={{ fontSize: 8, color: T.textMuted, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.type}</div>

        {/* Metrics */}
        {renderMetrics()}
      </div>
    </div>
  );
};

const nodeTypes = { fleetOps: FleetOpsNode, vesselGateway: VesselGatewayNode, shipSystem: ShipSystemNode };

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL — V3
// ════════════════════════════════════════════════════════════════════════════
const AIActivityPanel = ({ isOpen, onClose, anomalies, systems, vessel }) => {
  const [activeTab, setActiveTab] = useState('bridge');
  const [executingAction, setExecutingAction] = useState(null);
  if (!isOpen) return null;

  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    await new Promise(r => setTimeout(r, 2000));
    setExecutingAction(null);
  };

  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const tabs = [
    { id: 'bridge', label: '🚨 Bridge', badge: criticalCount },
    { id: 'cargo',  label: '📦 Cargo' },
    { id: 'engine', label: '⚙️ Engine' },
  ];

  return (
    <div style={{
      position: 'absolute', top: 0, right: 0, height: '100%', width: 420,
      background: T.bgCard, borderLeft: `1px solid ${T.border}`,
      boxShadow: '-8px 0 32px rgba(26,24,20,0.12)', zIndex: 50,
      display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.25s ease-out',
    }}>
      {/* Gold top rule */}
      <div style={{ height: 3, background: T.goldLight, flexShrink: 0 }} />

      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, flexShrink: 0, background: T.bgCard }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: T.navyBg, border: `1px solid ${T.navyBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🚢</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>AI Sentry Maritime</div>
              <div style={{ fontSize: 10, color: T.textMuted }}>{vessel.name} · {vessel.imo}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`, color: T.textSecondary, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 10, background: T.greenBg, border: `1px solid ${T.greenBorder}` }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.greenLight, animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontSize: 10, color: T.greenLight, fontWeight: 600 }}>{vessel.status}</span>
          </div>
          <span style={{ fontSize: 10, color: T.textMuted }}>→ {vessel.destination}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1, padding: '10px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              color: activeTab === tab.id ? T.gold : T.textMuted,
              borderBottom: activeTab === tab.id ? `2px solid ${T.goldLight}` : '2px solid transparent',
              transition: 'all 0.15s',
            }}>
            {tab.label}
            {tab.badge > 0 && (
              <span style={{ padding: '1px 5px', background: T.redLight, color: '#fff', fontSize: 9, borderRadius: 8, fontWeight: 700 }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {activeTab === 'bridge' && (
          anomalies.length === 0
            ? <div style={{ textAlign: 'center', color: T.textMuted, paddingTop: 40, fontSize: 13 }}>✓ All systems normal</div>
            : anomalies.map(anomaly => {
                const system = systems.find(s => s.id === anomaly.systemId);
                const cat = CATEGORY_STYLES[system?.category] || CATEGORY_STYLES.propulsion;
                const isCrit = anomaly.severity === 'critical';
                return (
                  <div key={anomaly.id} style={{ background: isCrit ? T.redBg : T.amberBg, border: `1px solid ${isCrit ? T.redBorder : T.amberBorder}`, borderRadius: 8, padding: 14, position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, background: isCrit ? T.redLight : T.amberLight, borderRadius: '8px 0 0 8px' }} />
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{cat.icon}</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{anomaly.systemId}</div>
                          <div style={{ fontSize: 10, color: T.textMuted }}>{system?.type}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: isCrit ? T.redLight : T.amberLight, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{anomaly.confidence}%</div>
                        <div style={{ fontSize: 9, color: T.textMuted }}>confidence</div>
                      </div>
                    </div>
                    <p style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5, margin: '0 0 8px' }}>{anomaly.description}</p>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontSize: 10, fontFamily: 'monospace', padding: '2px 8px', borderRadius: 4, background: isCrit ? T.redBg : T.amberBg, border: `1px solid ${isCrit ? T.redBorder : T.amberBorder}`, color: isCrit ? T.redLight : T.amberLight }}>{anomaly.metric}</span>
                    </div>
                    <div style={{ background: T.goldBg, border: `1px solid ${T.goldBorder}`, borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
                      <div style={{ fontSize: 8, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 4 }}>AI Recommendation</div>
                      <div style={{ fontSize: 11, color: T.textPrimary, lineHeight: 1.5 }}>{anomaly.recommendation}</div>
                    </div>
                    {anomaly.action && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleExecuteAction(anomaly)} disabled={executingAction === anomaly.id}
                          style={{ flex: 1, padding: '7px 12px', background: isCrit ? T.red : T.amber, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, opacity: executingAction === anomaly.id ? 0.7 : 1 }}>
                          {executingAction === anomaly.id ? '⟳ Executing...' : `✓ ${anomaly.action.type.replace(/_/g, ' ')}`}
                        </button>
                        <button style={{ padding: '7px 12px', background: T.bg, border: `1px solid ${T.border}`, color: T.textSecondary, fontSize: 11, borderRadius: 6, cursor: 'pointer' }}>Acknowledge</button>
                      </div>
                    )}
                  </div>
                );
              })
        )}

        {activeTab === 'cargo' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Cargo Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: T.gold, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{vessel.containersLoaded?.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3 }}>Containers Loaded</div>
                </div>
                <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: T.navy, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{vessel.reeferCount}</div>
                  <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3 }}>Reefer Units</div>
                </div>
              </div>
            </div>
            <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Reefer Status</div>
              {systems.filter(s => s.type === 'Reefer Monitoring').map(reefer => {
                const m = reefer.metrics;
                const alert = m.tempDeviation > 3;
                return (
                  <div key={reefer.id} style={{ background: T.bgCard, border: `1px solid ${alert ? T.redBorder : T.border}`, borderRadius: 6, padding: '10px 12px', marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, fontFamily: 'monospace' }}>{m.containerId}</div>
                        <div style={{ fontSize: 10, color: T.textMuted }}>{m.cargoType}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: alert ? T.redLight : T.greenLight, fontFamily: 'Georgia, serif', lineHeight: 1 }}>{m.actualTemp}°C</div>
                        <div style={{ fontSize: 9, color: T.textMuted }}>SP: {m.setpoint}°C</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'engine' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Propulsion Status</div>
              {systems.filter(s => s.type === 'Main Engine').map(engine => {
                const m = engine.metrics;
                const maxTemp = Math.max(...(m.cylExhaustTemps || [0]));
                const hotCyl = (m.cylExhaustTemps || []).indexOf(maxTemp) + 1;
                const tempAlert = maxTemp > m.avgExhaustTemp + 50;
                return (
                  <div key={engine.id} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{engine.id}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: T.greenLight }}>{m.load}% Load</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, textAlign: 'center' }}>
                      {[
                        { label: 'RPM',    value: m.rpm,                        color: T.navy      },
                        { label: 'Power',  value: `${(m.power/1000).toFixed(1)}MW`, color: T.gold },
                        { label: 'Fuel',   value: `${m.fuelRate}t/d`,           color: T.amberLight},
                        { label: 'Hot Cyl',value: `#${hotCyl}: ${maxTemp}°C`,   color: tempAlert ? T.redLight : T.textSecondary },
                      ].map(s => (
                        <div key={s.label}>
                          <div style={{ fontSize: 8, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: s.color, fontFamily: 'Georgia, serif' }}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10 }}>Bunker Status</div>
              {systems.filter(s => s.type === 'Heavy Fuel Oil').map(bunker => {
                const m = bunker.metrics;
                const pct = (m.currentLevel / m.totalCapacity * 100).toFixed(0);
                return (
                  <div key={bunker.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 10, color: T.textSecondary }}>HFO</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, fontFamily: 'Georgia, serif' }}>{m.currentLevel} / {m.totalCapacity} m³ ({pct}%)</span>
                    </div>
                    <div style={{ height: 6, background: T.border, borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{ height: '100%', background: T.goldLight, borderRadius: 3, width: `${pct}%` }} />
                    </div>
                    <div style={{ fontSize: 10, color: T.textMuted }}>{m.consumption24h} t/day · {m.daysRemaining.toFixed(1)} days remaining</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontSize: 10, color: T.textMuted }}>Last VSAT sync: 2s ago</span>
        <button style={{ fontSize: 10, fontWeight: 600, color: T.gold, background: 'none', border: 'none', cursor: 'pointer' }}>Refresh Now</button>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT — V3
// ════════════════════════════════════════════════════════════════════════════
const ProactiveAlert = ({ anomalies, systems, onViewDetails }) => {
  const critical = anomalies.filter(a => a.severity === 'critical');
  if (critical.length === 0) return null;
  const fireAnomaly = critical.find(a => a.type === 'FIRE_ALARM');
  const topAnomaly = fireAnomaly || critical[0];
  const system = systems.find(s => s.id === topAnomaly.systemId);
  const cat = CATEGORY_STYLES[system?.category] || CATEGORY_STYLES.safety;

  return (
    <div style={{ position: 'absolute', top: 100, left: 20, zIndex: 40, animation: 'slideInLeft 0.3s ease-out' }}>
      <div style={{ background: T.bgCard, border: `1px solid ${T.redBorder}`, borderRadius: 10, padding: 16, maxWidth: 380, boxShadow: '0 8px 24px rgba(139,26,26,0.12)', overflow: 'hidden', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: T.redLight }} />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.redBg, border: `1px solid ${T.redBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 20, animation: 'pulse 1.5s infinite' }}>
            {cat.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: T.red, fontFamily: 'Georgia, serif', marginBottom: 4 }}>
              {topAnomaly.type === 'FIRE_ALARM' ? '🔥 FIRE ALARM' : topAnomaly.type === 'REEFER_TEMP_ALARM' ? '❄️ REEFER ALARM' : 'VESSEL ALERT'}
            </div>
            <p style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5, margin: '0 0 6px' }}>{topAnomaly.description}</p>
            <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 10 }}>
              {topAnomaly.systemId} {critical.length > 1 ? `· +${critical.length - 1} more` : `· ${topAnomaly.metric}`}
            </div>
            <button onClick={onViewDetails} style={{ padding: '6px 14px', background: T.red, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 6, border: 'none', cursor: 'pointer' }}>
              View Bridge Panel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR — V3
// ════════════════════════════════════════════════════════════════════════════
const StatsBar = ({ vessel, anomalies, onOpenPanel }) => {
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {[
        { value: vessel.speed, label: 'knots', color: T.navy },
        { value: `${vessel.heading}°`, label: 'HDG', color: T.textSecondary },
        { value: vessel.containersLoaded?.toLocaleString(), label: 'TEU', color: T.gold },
      ].map(s => (
        <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 5, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 10px', boxShadow: '0 1px 3px rgba(26,24,20,0.06)' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: s.color, fontFamily: 'Georgia, serif' }}>{s.value}</span>
          <span style={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</span>
        </div>
      ))}
      {criticalCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: T.redBg, border: `1px solid ${T.redBorder}`, borderRadius: 6, padding: '5px 10px', animation: 'pulse 1.5s infinite' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: T.redLight, fontFamily: 'Georgia, serif' }}>{criticalCount}</span>
          <span style={{ fontSize: 9, color: T.redLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Alerts</span>
        </div>
      )}
      <button onClick={onOpenPanel} style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.goldBg, border: `1px solid ${T.goldBorder}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>
        <span style={{ fontSize: 13 }}>🤖</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: T.gold }}>AI Sentry</span>
        {anomalies.length > 0 && <span style={{ background: T.goldLight, color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 8, padding: '1px 5px' }}>{anomalies.length}</span>}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND — V3
// ════════════════════════════════════════════════════════════════════════════
const Legend = () => (
  <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', boxShadow: '0 2px 8px rgba(26,24,20,0.08)' }}>
    <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 8 }}>Ship Systems</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
      {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: style.color, flexShrink: 0 }} />
          <span style={{ fontSize: 9, color: T.textSecondary }}>{style.label}</span>
        </div>
      ))}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// CATEGORY FILTER BAR
// ════════════════════════════════════════════════════════════════════════════
const CategoryFilter = ({ active, onChange }) => {
  const cats = ['All', ...Object.keys(CATEGORY_STYLES)];
  return (
    <div style={{ display: 'flex', gap: 4, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: 4, boxShadow: '0 1px 3px rgba(26,24,20,0.06)' }}>
      {cats.map(cat => {
        const style = cat !== 'All' ? CATEGORY_STYLES[cat] : null;
        const isActive = active === cat;
        return (
          <button key={cat} onClick={() => onChange(cat)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600,
              background: isActive ? (style ? `${style.color}12` : T.bgElevated) : 'transparent',
              color: isActive ? (style ? style.color : T.textPrimary) : T.textMuted,
              borderBottom: isActive ? `2px solid ${style ? style.color : T.textPrimary}` : '2px solid transparent',
              transition: 'all 0.15s',
            }}>
            {style && <span style={{ width: 5, height: 5, borderRadius: '50%', background: style.color, display: 'inline-block' }} />}
            {cat === 'All' ? 'All' : style.label}
          </button>
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
const MaritimeSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [systems] = useState(MOCK_SYSTEMS);
  const [vessel] = useState(MOCK_VESSEL_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('All');

  const buildTopology = useCallback((filter = 'All') => {
    const detectedAnomalies = detectMaritimeAnomalies(systems, vessel);
    setAnomalies(detectedAnomalies);
    const newNodes = [];
    const newEdges = [];
    const CLOUD_Y = 0, VESSEL_Y = 200, SYSTEM_Y_START = 450, CENTER_X = 700, SYSTEM_GAP = 175, CATEGORY_GAP = 155;
    const categories = ['propulsion', 'navigation', 'power', 'cargo', 'stability', 'bunkers', 'hull', 'safety', 'communications'];

    newNodes.push({ id: 'fleet-ops', type: 'fleetOps', position: { x: CENTER_X, y: CLOUD_Y }, data: { label: 'Fleet Operations', sublabel: 'Shore HQ - Singapore', vessels: 24, atSea: 18, alerts: detectedAnomalies.length } });
    newNodes.push({ id: `vessel-${vessel.id}`, type: 'vesselGateway', position: { x: CENTER_X, y: VESSEL_Y }, data: vessel });
    newEdges.push({ id: 'fleet-to-vessel', source: 'fleet-ops', target: `vessel-${vessel.id}`, type: 'smoothstep', animated: true, style: { stroke: T.navy, strokeWidth: 2 } });

    let currentY = SYSTEM_Y_START;
    const filteredCategories = filter === 'All' ? categories : categories.filter(c => c === filter);
    filteredCategories.forEach(category => {
      const categorySystems = systems.filter(s => s.category === category);
      if (categorySystems.length === 0) return;
      const startX = CENTER_X - ((categorySystems.length - 1) * SYSTEM_GAP) / 2;
      categorySystems.forEach((system, idx) => {
        const aiStatus = getSystemAIStatus(system, detectedAnomalies);
        const systemId = `system-${system.id}`;
        newNodes.push({ id: systemId, type: 'shipSystem', position: { x: startX + idx * SYSTEM_GAP, y: currentY }, data: { ...system, aiStatus } });
        const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
        const edgeColor = hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? T.redLight : T.amberLight) : T.navy;
        newEdges.push({ id: `vessel-to-${systemId}`, source: `vessel-${vessel.id}`, target: systemId, type: 'smoothstep', animated: system.status !== 'standby', style: { stroke: edgeColor, strokeWidth: 1.5 } });
      });
      currentY += CATEGORY_GAP;
    });
    return { nodes: newNodes, edges: newEdges };
  }, [systems, vessel]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const { nodes: n, edges: e } = buildTopology(categoryFilter);
      setNodes(n); setEdges(e); setLoading(false);
    }, 800);
  }, [buildTopology, setNodes, setEdges, categoryFilter]);

  const handleFilterChange = (cat) => {
    setCategoryFilter(cat);
    setLoading(true);
    setTimeout(() => {
      const { nodes: n, edges: e } = buildTopology(cat);
      setNodes(n); setEdges(e); setLoading(false);
    }, 400);
  };

  return (
    <div style={{ height: 'calc(100vh - 64px)', width: '100%', background: T.bg, position: 'relative', overflow: 'hidden', fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif" }}>

      {/* Header */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 40, background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: '0 24px', boxShadow: '0 1px 4px rgba(26,24,20,0.06)' }}>
        {/* Gold top rule */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${T.goldLight} 0%, ${T.gold} 30%, transparent 70%)` }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0 12px' }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.25em', marginBottom: 4 }}>AI Sentry · Maritime</div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: 'Georgia, serif', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 34, height: 34, borderRadius: 6, background: T.navyBg, border: `1px solid ${T.navyBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🚢</span>
              Maritime Sentry
            </h1>
            <p style={{ fontSize: 11, color: T.textSecondary, margin: '4px 0 0' }}>AI-Powered Vessel Operations · Navigation · Cargo · Safety</p>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <span style={{ fontSize: 9, padding: '2px 8px', background: T.navyBg, border: `1px solid ${T.navyBorder}`, borderRadius: 10, color: T.navy, fontWeight: 600 }}>{vessel.name} · {vessel.flag}</span>
              <span style={{ fontSize: 9, padding: '2px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, color: T.textMuted }}>MWC Barcelona 2025</span>
            </div>
          </div>
          <StatsBar vessel={vessel} anomalies={anomalies} onOpenPanel={() => setIsAIPanelOpen(true)} />
        </div>
        {/* Filter bar */}
        <div style={{ paddingBottom: 12 }}>
          <CategoryFilter active={categoryFilter} onChange={handleFilterChange} />
        </div>
      </div>

      <ProactiveAlert anomalies={anomalies} systems={systems} onViewDetails={() => setIsAIPanelOpen(true)} />

      {/* Loading */}
      {loading && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,243,239,0.9)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.gold}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, fontFamily: 'Georgia, serif' }}>AI Sentry Initializing</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Connecting via VSAT</div>
          </div>
        </div>
      )}

      {/* ReactFlow — warm canvas */}
      {!loading && (
        <div style={{ position: 'absolute', inset: 0, top: 145 }}>
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView fitViewOptions={{ padding: 0.1 }}
            minZoom={0.15} maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.35 }}
            proOptions={{ hideAttribution: true }}
            style={{ background: T.bgCanvas }}
          >
            <Background variant="dots" gap={20} size={1} color="rgba(154,123,46,0.12)" />
            <Controls style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: '0 2px 8px rgba(26,24,20,0.08)' }} />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'fleetOps') return T.navy;
                if (node.type === 'vesselGateway') return T.navy;
                if (node.data?.aiStatus === AI_STATUS.CRITICAL) return T.redLight;
                if (node.data?.aiStatus === AI_STATUS.WARNING) return T.amberLight;
                return T.greenLight;
              }}
              maskColor="rgba(237,233,226,0.7)"
              style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8 }}
            />
            <Panel position="bottom-left"><Legend /></Panel>
          </ReactFlow>
        </div>
      )}

      <AIActivityPanel isOpen={isAIPanelOpen} onClose={() => setIsAIPanelOpen(false)} anomalies={anomalies} systems={systems} vessel={vessel} />

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideInLeft  { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes pulse        { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes spin         { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .react-flow__controls button { background: ${T.bgCard} !important; border-color: ${T.border} !important; color: ${T.textSecondary} !important; }
        .react-flow__controls button:hover { background: ${T.bgElevated} !important; }
      `}</style>
    </div>
  );
};

export default MaritimeSentry;
